/**
 * ConfigEditor Component
 *
 * Unified config editor with seamless code/form view toggle.
 * Supports all config types: eligibility, ranking, branding, journey, schema.
 */

import Editor from "@monaco-editor/react";
import type {
  EligibilityConfig,
  RankingConfig,
  BrandingConfig,
  JourneyConfig,
} from "@tatvex/config";
import { useCallback, useState, useMemo } from "react";

import { BrandingFormBuilder } from "./BrandingFormBuilder";
import { EligibilityFormBuilder } from "./EligibilityFormBuilder";
import { JourneyFormBuilder } from "./JourneyFormBuilder";
import { RankingFormBuilder } from "./RankingFormBuilder";
import { SchemaFormBuilder } from "./SchemaFormBuilder";
import { ViewModeToggle } from "./ViewModeToggle";
import type {
  ViewMode,
  ConfigEditorProps,
  SchemaFormValue,
  FieldError,
  ValidationResult,
} from "./types";

// ============================================================================
// JSON Code Editor (Monaco)
// ============================================================================

interface JsonCodeEditorProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly readOnly?: boolean;
  readonly errors?: readonly FieldError[];
}

function JsonCodeEditor({
  value,
  onChange,
  readOnly = false,
  errors = [],
}: JsonCodeEditorProps): React.JSX.Element {
  const [parseError, setParseError] = useState<string | null>(null);

  const handleChange = useCallback(
    (newValue: string | undefined) => {
      const val = newValue ?? "";
      try {
        JSON.parse(val);
        setParseError(null);
        onChange(val);
      } catch (e) {
        setParseError(e instanceof Error ? e.message : "Invalid JSON");
        // Still update parent so they have the raw text
      }
    },
    [onChange]
  );

  return (
    <div className="json-code-editor flex flex-col gap-2">
      <div className="overflow-hidden rounded-md border border-gray-300">
        <Editor
          height="400px"
          language="json"
          theme="vs-dark"
          value={value}
          onChange={handleChange}
          options={{
            readOnly,
            minimap: { enabled: false },
            fontSize: 14,
            fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
            lineNumbers: "on",
            renderWhitespace: "selection",
            tabSize: 2,
            wordWrap: "on",
            formatOnPaste: true,
            formatOnType: true,
            automaticLayout: true,
            scrollBeyondLastLine: false,
          }}
        />
      </div>

      {parseError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <strong>JSON Parse Error:</strong> {parseError}
        </div>
      )}

      {errors.length > 0 && (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-700">
          <strong>Validation Errors:</strong>
          <ul className="mt-1 list-inside list-disc">
            {errors.map((err, idx) => (
              <li key={idx}>
                <code>{err.path}</code>: {err.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

type ConfigValue =
  | EligibilityConfig
  | RankingConfig
  | BrandingConfig
  | JourneyConfig
  | SchemaFormValue;

/**
 * ConfigEditor - Unified config editor with code/form toggle
 *
 * CSS Classes:
 * - config-editor: Container element
 * - config-editor__header: Header with view toggle
 * - config-editor__content: Editor content area
 */
export function ConfigEditor<T extends ConfigValue>({
  value,
  onChange,
  onValidate,
  configType,
  defaultMode = "form",
  readOnly = false,
  className = "",
}: ConfigEditorProps<T>): React.JSX.Element {
  const [viewMode, setViewMode] = useState<ViewMode>(defaultMode);
  const [errors, setErrors] = useState<readonly FieldError[]>([]);

  // Serialize value to JSON for code view
  const jsonValue = useMemo(() => JSON.stringify(value, null, 2), [value]);

  // Handle JSON change from code view
  const handleJsonChange = useCallback(
    (json: string) => {
      try {
        const parsed = JSON.parse(json) as T;
        if (onValidate) {
          const result = onValidate(parsed);
          setErrors(result.errors);
        }
        onChange(parsed);
      } catch {
        // Ignore parse errors - they're shown in the code editor
      }
    },
    [onChange, onValidate]
  );

  // Handle form change
  const handleFormChange = useCallback(
    (newValue: T) => {
      if (onValidate) {
        const result = onValidate(newValue);
        setErrors(result.errors);
      }
      onChange(newValue);
    },
    [onChange, onValidate]
  );

  // Validation handler for form builders
  const handleValidate = useCallback(
    (val: ConfigValue): ValidationResult => {
      if (onValidate) {
        return onValidate(val as T);
      }
      return { valid: true, errors: [] };
    },
    [onValidate]
  );

  // Render the appropriate form builder based on config type
  const renderFormBuilder = (): React.JSX.Element => {
    switch (configType) {
      case "eligibility":
        return (
          <EligibilityFormBuilder
            value={value as EligibilityConfig}
            onChange={handleFormChange as (v: EligibilityConfig) => void}
            onValidate={handleValidate as (v: EligibilityConfig) => ValidationResult}
            readOnly={readOnly}
            errors={errors}
          />
        );

      case "ranking":
        return (
          <RankingFormBuilder
            value={value as RankingConfig}
            onChange={handleFormChange as (v: RankingConfig) => void}
            onValidate={handleValidate as (v: RankingConfig) => ValidationResult}
            readOnly={readOnly}
            errors={errors}
          />
        );

      case "branding":
        return (
          <BrandingFormBuilder
            value={value as BrandingConfig}
            onChange={handleFormChange as (v: BrandingConfig) => void}
            onValidate={handleValidate as (v: BrandingConfig) => ValidationResult}
            readOnly={readOnly}
            errors={errors}
          />
        );

      case "journey":
        return (
          <JourneyFormBuilder
            value={value as JourneyConfig}
            onChange={handleFormChange as (v: JourneyConfig) => void}
            onValidate={handleValidate as (v: JourneyConfig) => ValidationResult}
            readOnly={readOnly}
            errors={errors}
          />
        );

      case "schema":
        return (
          <SchemaFormBuilder
            value={value as SchemaFormValue}
            onChange={handleFormChange as (v: SchemaFormValue) => void}
            onValidate={handleValidate as (v: SchemaFormValue) => ValidationResult}
            readOnly={readOnly}
            errors={errors}
          />
        );

      default:
        return (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-700">
            Unknown config type: {configType}
          </div>
        );
    }
  };

  return (
    <div className={`config-editor flex flex-col gap-4 ${className}`}>
      {/* Header with view toggle */}
      <div className="config-editor__header flex items-center justify-between">
        <h2 className="text-lg font-medium capitalize text-gray-900">
          {configType.replace(/_/g, " ")} Configuration
        </h2>
        <ViewModeToggle mode={viewMode} onChange={setViewMode} disabled={readOnly} />
      </div>

      {/* Content */}
      <div className="config-editor__content">
        {viewMode === "code" ? (
          <JsonCodeEditor
            value={jsonValue}
            onChange={handleJsonChange}
            readOnly={readOnly}
            errors={errors}
          />
        ) : (
          renderFormBuilder()
        )}
      </div>
    </div>
  );
}

export type { ConfigEditorProps };
